import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

export const GooglePlacesAutocompleteContext = React.createContext({
  autocompleteService: null,
  generateToken: () => {},
  placesService: null,
  sessionToken: null,
});

const GooglePlacesAutocompleteProvider = ({
  children,
  googlePlacesAutoCompleteKey,
  googlePlacesAutocompleteEnabled,
}) => {
  const [autocompleteService, setAutocompleteService] = useState(null);
  const [placesService, setPlacesService] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const updateServices = useCallback(() => {
    if (window.google) {
      if (!autocompleteService) {
        setAutocompleteService(new window.google.maps.places.AutocompleteService());
      }
      if (!placesService) {
        setPlacesService(
          new window.google.maps.places.PlacesService(document.createElement('div')),
        );
      }

      setEnabled(true);
    }
  }, [autocompleteService, placesService]);

  const generateToken = () => {
    setSessionToken(
      window.google ? new window.google.maps.places.AutocompleteSessionToken() : null,
    );
  };

  useEffect(() => {
    const script = document.createElement('script');

    if (googlePlacesAutocompleteEnabled && !initialized) {
      setInitialized(true);
      try {
        script.src = `https://maps.googleapis.com/maps/api/js?key=${googlePlacesAutoCompleteKey}&libraries=places`;
        script.async = true;
        script.id = 'googlePlacesAutocomplete';
        script.onload = async () => updateServices();
        document.body.appendChild(script);
      } catch (e) {
        window.console.log(e);
      }
    }

    return () => {
      const scriptToRemove = document.querySelector('#googlePlacesAutocomplete');
      if (scriptToRemove) {
        scriptToRemove.parentNode.removeChild(scriptToRemove);
      }
    };
  }, [googlePlacesAutoCompleteKey, googlePlacesAutocompleteEnabled, initialized, updateServices]);

  return (
    <GooglePlacesAutocompleteContext.Provider
      value={{
        autocompleteService,
        enabled,
        generateToken,
        placesService,
        sessionToken,
      }}
    >
      {children}
    </GooglePlacesAutocompleteContext.Provider>
  );
};

GooglePlacesAutocompleteProvider.propTypes = {
  children: PropTypes.node.isRequired,
  googlePlacesAutoCompleteKey: PropTypes.string,
  googlePlacesAutocompleteEnabled: PropTypes.bool,
};

GooglePlacesAutocompleteProvider.defaultProps = {
  googlePlacesAutoCompleteKey: null,
  googlePlacesAutocompleteEnabled: false,
};

const mapStateToProps = ({ session }) => ({
  // eslint-disable-next-line camelcase
  googlePlacesAutoCompleteKey: session.meta?.configs?.google_places_autocomplete_api_key,
  // eslint-disable-next-line camelcase
  googlePlacesAutocompleteEnabled: session.meta?.configs?.google_places_autocomplete,
});

export default connect(mapStateToProps)(GooglePlacesAutocompleteProvider);
